import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Check, Search } from "lucide-react";
import { ATECO_CODES, INPS_TYPES } from "../../constants/ateco";
import { getAliquotaINPS } from "../../constants/aliquote";
import { calcolaAnniAttivita, getAliquotaSostitutiva } from "../../utils/fiscalEngine";

const currentYear = new Date().getFullYear();

function formatPercent(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "percent",
    minimumFractionDigits: 2,
  }).format(value / 100);
}

// Converte stringa italiana in numero
function italianToNumber(str) {
  if (!str) return 0;
  let cleaned = str.replace(/\s/g, "");
  if (cleaned.includes(".") && cleaned.includes(",")) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(",")) {
    cleaned = cleaned.replace(",", ".");
  }
  return parseFloat(cleaned) || 0;
}

export default function OnboardingWizard({ darkMode, fiscalSettings, setFiscalSettings, setFiscalDataForYear, updateAppSettings }) {
  const [step, setStep] = useState(0);

  // Step 1: Anno apertura P.IVA (+ data esatta, per il ragguaglio soglie primo anno)
  const [pivaYearStart, setPivaYearStart] = useState(fiscalSettings.pivaYearStart || currentYear);
  const [pivaStartDate, setPivaStartDate] = useState(fiscalSettings.pivaStartDate || "");

  // Step 2: Codice ATECO
  const [atecoSearch, setAtecoSearch] = useState("");
  const [atecoCode, setAtecoCode] = useState(fiscalSettings.atecoCode || "");
  const [profitabilityCoefficient, setProfitabilityCoefficient] = useState(fiscalSettings.profitabilityCoefficient || 0.78);

  // Step 3: Gestione INPS
  const [inpsType, setInpsType] = useState(fiscalSettings.inpsType || "gestione-separata");

  // Step 4: Contributi anno precedente
  const [contributiInput, setContributiInput] = useState("");
  const [noContributi, setNoContributi] = useState(false);

  // Step 5: Dati pregressi anno precedente (opzionali)
  const [incassiInput, setIncassiInput] = useState("");
  const [accontiInput, setAccontiInput] = useState("");

  // Calcoli. Qui l'anno di riferimento è quello solare: l'onboarding descrive la
  // situazione dell'utente adesso ("Sei al N° anno"), non un anno storico.
  const anniAttivita = calcolaAnniAttivita(pivaYearStart, currentYear);
  const aliquota = getAliquotaSostitutiva(pivaYearStart, currentYear);
  const needsContributi = currentYear - pivaYearStart >= 1;
  const totalSteps = needsContributi ? 5 : 3;

  const filteredAteco = useMemo(() => {
    if (!atecoSearch.trim()) return ATECO_CODES;
    const q = atecoSearch.toLowerCase();
    return ATECO_CODES.filter(
      (a) => a.code.toLowerCase().includes(q) || a.label.toLowerCase().includes(q)
    );
  }, [atecoSearch]);

  const canProceed = () => {
    if (step === 0) return pivaYearStart >= 1990 && pivaYearStart <= currentYear;
    if (step === 1) return atecoCode && profitabilityCoefficient > 0;
    if (step === 2) return !!inpsType;
    if (step === 3) return noContributi || italianToNumber(contributiInput) > 0;
    return true;
  };

  const handleComplete = () => {
    const contributi = noContributi ? 0 : italianToNumber(contributiInput);
    setFiscalSettings({
      ...fiscalSettings,
      pivaYearStart,
      // Salva la data solo se coerente con l'anno scelto (altrimenti resta vuota → 1° gennaio).
      pivaStartDate: pivaStartDate && Number(pivaStartDate.slice(0, 4)) === pivaYearStart ? pivaStartDate : "",
      atecoCode,
      profitabilityCoefficient,
      taxRate: aliquota,
      inpsType,
      contributiInpsPrecedenti: contributi,
    });
    // Salva i dati pregressi dell'anno precedente in fiscalByYear[anno-1]
    if (needsContributi && setFiscalDataForYear) {
      setFiscalDataForYear(currentYear - 1, {
        incassi: italianToNumber(incassiInput),
        accontiVersatiManuali: italianToNumber(accontiInput),
        contributiInpsVersati: contributi,
      });
    }
    updateAppSettings({
      onboardingCompleted: true,
      hasConfiguredFiscal: true,
      carryOverConfirmedYear: currentYear,
    });
  };

  const next = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  // Years dropdown
  const years = [];
  for (let y = currentYear; y >= 1990; y--) years.push(y);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: darkMode ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.5)' }}>
      <div className={`w-full max-w-lg rounded-2xl shadow-2xl border overflow-hidden ${
        darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'
      }`}>
        {/* Header */}
        <div className={`px-6 pt-6 pb-4 ${darkMode ? 'border-neutral-800' : 'border-neutral-200'}`}>
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
            Configurazione iniziale
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
            Impostiamo il tuo profilo fiscale in pochi passaggi
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-2 mt-4">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all ${
                  i === step
                    ? 'w-8 bg-forfy-500'
                    : i < step
                      ? 'w-2 bg-forfy-400'
                      : darkMode ? 'w-2 bg-neutral-700' : 'w-2 bg-neutral-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 min-h-[320px]">
          {/* Step 1: Anno apertura */}
          {step === 0 && (
            <div className="space-y-4">
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                In che anno hai aperto la Partita IVA?
              </h3>

              <select
                value={pivaYearStart}
                onChange={(e) => setPivaYearStart(Number(e.target.value))}
                className={`w-full px-4 py-3 rounded-xl border-2 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-white'
                    : 'border-neutral-300 bg-white text-neutral-900'
                }`}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              {/* Data esatta: serve a ragguagliare le soglie nel primo anno */}
              {anniAttivita === 1 && (
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    Data esatta di apertura (opzionale)
                  </label>
                  <input
                    type="date"
                    value={pivaStartDate}
                    onChange={(e) => setPivaStartDate(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border-2 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 ${
                      darkMode
                        ? 'border-neutral-700 bg-neutral-800 text-white'
                        : 'border-neutral-300 bg-white text-neutral-900'
                    }`}
                  />
                  <p className={`mt-1 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                    Nel primo anno la soglia degli 85.000 € è ragguagliata ai giorni effettivi di attività.
                  </p>
                </div>
              )}

              <div className={`p-4 rounded-xl ${darkMode ? 'bg-neutral-800' : 'bg-neutral-50'}`}>
                <div className={`text-sm ${darkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                  Sei al <strong>{anniAttivita}° anno</strong> di attività
                </div>
                <div className={`text-sm mt-2 ${darkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                  Aliquota imposta sostitutiva:{" "}
                  <span className={`font-bold ${aliquota === 5 ? 'text-emerald-500' : 'text-amber-500'}`}>
                    {aliquota}%
                  </span>
                  {aliquota === 5 && (
                    <span className={`ml-1 text-xs ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      (agevolata startup, primi 5 anni)
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Codice ATECO */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Qual è il tuo codice ATECO?
              </h3>

              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-neutral-500' : 'text-neutral-400'}`} />
                <input
                  type="text"
                  placeholder="Cerca per codice o attività..."
                  value={atecoSearch}
                  onChange={(e) => setAtecoSearch(e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 ${
                    darkMode
                      ? 'border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500'
                      : 'border-neutral-300 bg-white text-neutral-900 placeholder-neutral-400'
                  }`}
                />
              </div>

              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredAteco.map((ateco) => (
                  <button
                    key={ateco.code}
                    type="button"
                    onClick={() => {
                      setAtecoCode(ateco.code);
                      setProfitabilityCoefficient(ateco.coefficient);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm transition-all ${
                      atecoCode === ateco.code
                        ? darkMode
                          ? 'bg-forfy-900/50 border-2 border-forfy-500 text-white'
                          : 'bg-forfy-50 border-2 border-forfy-500 text-neutral-900'
                        : darkMode
                          ? 'border-2 border-neutral-700 text-neutral-300 hover:border-neutral-600'
                          : 'border-2 border-neutral-200 text-neutral-700 hover:border-neutral-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{ateco.code}</span>
                        <span className="mx-2">·</span>
                        <span>{ateco.label}</span>
                      </div>
                      {atecoCode === ateco.code && (
                        <Check className="w-4 h-4 text-forfy-500 shrink-0" />
                      )}
                    </div>
                    <div className={`text-xs mt-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                      Coeff. redditività: {Math.round(ateco.coefficient * 100)}%
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Gestione INPS */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Come versi i contributi INPS?
              </h3>

              <div className="space-y-3">
                {INPS_TYPES.map((type) => {
                  const rate = getAliquotaINPS(type.id, currentYear);
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setInpsType(type.id)}
                      className={`w-full text-left px-4 py-4 rounded-xl text-sm transition-all ${
                        inpsType === type.id
                          ? darkMode
                            ? 'bg-forfy-900/50 border-2 border-forfy-500 text-white'
                            : 'bg-forfy-50 border-2 border-forfy-500 text-neutral-900'
                          : darkMode
                            ? 'border-2 border-neutral-700 text-neutral-300 hover:border-neutral-600'
                            : 'border-2 border-neutral-200 text-neutral-700 hover:border-neutral-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className={`text-xs mt-1 ${darkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                            {type.description}
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-4">
                          <div className="font-bold">{formatPercent(rate)}</div>
                          {inpsType === type.id && (
                            <Check className="w-4 h-4 text-forfy-500 ml-auto mt-1" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Contributi anno precedente */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Contributi INPS versati nel {currentYear - 1} (solo sezione INPS)
              </h3>
              <p className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                Solo la parte INPS dei tuoi F24. Un F24 contiene anche la sezione Erario
                (imposta sostitutiva): quella <strong>non</strong> va qui, la inserisci nella
                schermata successiva. Questo importo viene dedotto dall'imponibile per il
                calcolo dell'imposta sostitutiva.
              </p>

              <div className="relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={contributiInput}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9,.]/g, "");
                    setContributiInput(value);
                    if (value) setNoContributi(false);
                  }}
                  disabled={noContributi}
                  placeholder="0,00"
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 ${
                    noContributi
                      ? darkMode ? 'border-neutral-700 bg-neutral-800/50 text-neutral-600' : 'border-neutral-200 bg-neutral-100 text-neutral-400'
                      : darkMode
                        ? 'border-neutral-700 bg-neutral-800 text-white'
                        : 'border-neutral-300 bg-white text-neutral-900'
                  }`}
                />
              </div>

              <label className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
                noContributi
                  ? darkMode
                    ? 'border-forfy-500 bg-forfy-900/30 text-white'
                    : 'border-forfy-500 bg-forfy-50 text-neutral-900'
                  : darkMode
                    ? 'border-neutral-700 text-neutral-300 hover:border-neutral-600'
                    : 'border-neutral-200 text-neutral-700 hover:border-neutral-300'
              }`}>
                <input
                  type="checkbox"
                  checked={noContributi}
                  onChange={(e) => {
                    setNoContributi(e.target.checked);
                    if (e.target.checked) setContributiInput("");
                  }}
                  className="rounded"
                />
                <span className="text-sm">Non ho versato contributi nel {currentYear - 1}</span>
              </label>
            </div>
          )}

          {/* Step 5: Dati pregressi anno precedente */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Dati dell'anno precedente ({currentYear - 1})
              </h3>
              <p className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                Facoltativi: servono a calcolare saldo e acconti di quest'anno. Puoi lasciarli vuoti e inserirli più tardi.
              </p>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                  Incassi totali {currentYear - 1}
                </label>
                <div className="relative">
                  <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>€</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={incassiInput}
                    onChange={(e) => setIncassiInput(e.target.value.replace(/[^0-9,.]/g, ""))}
                    placeholder="0,00"
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 ${
                      darkMode ? 'border-neutral-700 bg-neutral-800 text-white' : 'border-neutral-300 bg-white text-neutral-900'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                  Acconti imposta sostitutiva versati {currentYear - 1} (solo sezione Erario)
                </label>
                <div className="relative">
                  <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>€</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={accontiInput}
                    onChange={(e) => setAccontiInput(e.target.value.replace(/[^0-9,.]/g, ""))}
                    placeholder="0,00"
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 ${
                      darkMode ? 'border-neutral-700 bg-neutral-800 text-white' : 'border-neutral-300 bg-white text-neutral-900'
                    }`}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 flex items-center justify-between border-t ${
          darkMode ? 'border-neutral-800' : 'border-neutral-200'
        }`}>
          <div>
            {step > 0 && (
              <button
                type="button"
                onClick={back}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${
                  darkMode
                    ? 'text-neutral-400 hover:bg-neutral-800'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Indietro
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={next}
            disabled={!canProceed()}
            className={`flex items-center gap-1 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              canProceed()
                ? 'bg-forfy-500 text-white hover:bg-forfy-600'
                : darkMode
                  ? 'bg-neutral-800 text-neutral-600 cursor-not-allowed'
                  : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
            }`}
          >
            {step === totalSteps - 1 ? (
              <>
                Completa
                <Check className="w-4 h-4" />
              </>
            ) : (
              <>
                Avanti
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
